'use client'

import { useEffect, useState } from 'react'

type HighlightState = {
  selector: string
  label?: string
} | null

export default function CommunityDemoHighlight() {
  const [highlight, setHighlight] = useState<HighlightState>(null)
  const [rect, setRect] = useState<DOMRect | null>(null)

  useEffect(() => {
    function updateRect(selector: string) {
      const el = document.querySelector(selector) as HTMLElement | null
      if (!el) {
        setRect(null)
        return
      }
      setRect(el.getBoundingClientRect())
    }

    function onShow(event: Event) {
      const custom = event as CustomEvent
      const selector = String(custom.detail?.selector || '')
      const label = String(custom.detail?.label || '')
      if (!selector) return

      setHighlight({ selector, label })
      updateRect(selector)
    }

    function onHide() {
      setHighlight(null)
      setRect(null)
    }

    function onResizeOrScroll() {
      if (!highlight?.selector) return
      updateRect(highlight.selector)
    }

    window.addEventListener('community:demo-highlight', onShow as EventListener)
    window.addEventListener('community:demo-highlight-clear', onHide as EventListener)
    window.addEventListener('resize', onResizeOrScroll)
    window.addEventListener('scroll', onResizeOrScroll, true)

    return () => {
      window.removeEventListener('community:demo-highlight', onShow as EventListener)
      window.removeEventListener('community:demo-highlight-clear', onHide as EventListener)
      window.removeEventListener('resize', onResizeOrScroll)
      window.removeEventListener('scroll', onResizeOrScroll, true)
    }
  }, [highlight?.selector])

  if (!highlight || !rect) return null

  return (
    <>
      <div className="fixed inset-0 z-[10060] pointer-events-none bg-black/20" />
      <div
        className="fixed z-[10070] rounded-xl ring-4 ring-primary shadow-[0_0_0_9999px_rgba(0,0,0,0.35)] pointer-events-none"
        style={{
          top: rect.top - 8,
          left: rect.left - 8,
          width: rect.width + 16,
          height: rect.height + 16,
        }}
      />
      {highlight.label ? (
        <div
          className="fixed z-[10080] rounded-lg bg-background border px-3 py-2 text-sm shadow-xl"
          style={{
            top: Math.max(16, rect.bottom + 12),
            left: rect.left,
          }}
        >
          {highlight.label}
        </div>
      ) : null}
    </>
  )
}